import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Badge, Card, ErrorState, Loading, PageHeader } from '@/components/ui';
import { formatMoney, formatNumber, titleCase } from '@/lib/format';

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  pricing_model: string;
  base_price: string;
  currency: string;
  billing_interval: string;
  included_minutes: number;
  included_calls: number;
  overage_per_minute: string;
  price_multiplier: string;
  limits: Record<string, number>;
  active: boolean;
}

const PRICING_MODELS: Array<[string, string]> = [
  ['MULTIPLIER', 'Provider cost × multiplier. Pure usage, no floor.'],
  ['PER_MINUTE', 'Flat rate per billable minute.'],
  ['PER_CALL', 'Flat rate per call handled.'],
  ['PER_LEAD', 'Flat rate per qualified lead.'],
  ['PER_TRANSFER', 'Flat rate per transfer actually connected.'],
  ['MONTHLY_PLAN', 'Fixed monthly fee, usage included.'],
  ['HYBRID', 'Monthly base plus overage past the included minutes.'],
];

export default function Plans() {
  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: () => api.get<{ data: Plan[] }>('/admin/plans'),
  });

  if (isPending) return <Loading />;
  if (error) return <ErrorState error={error} retry={() => refetch()} />;

  return (
    <div className="w-full">
      <PageHeader
        title="Plans"
        description="What clients can be put on, and how each one bills."
      />

      <div className="grid gap-4 lg:grid-cols-3">
        {data.data.map((plan) => (
          <Card key={plan.id}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-semibold text-ink-900 dark:text-ink-50">{plan.name}</h3>
                <p className="font-mono text-xs text-ink-500">{plan.code}</p>
              </div>
              <Badge tone={plan.active ? 'green' : 'gray'}>
                {plan.active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            <p className="mt-2 text-2xl font-semibold tabular-nums text-ink-900 dark:text-ink-50">
              {formatMoney(plan.base_price, plan.currency)}
              <span className="ml-1 text-xs font-normal text-ink-500">
                /{plan.billing_interval.toLowerCase()}
              </span>
            </p>

            {plan.description && (
              <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">{plan.description}</p>
            )}

            <dl className="mt-3 space-y-1.5 border-t border-ink-200 pt-3 text-xs dark:border-ink-800">
              <Row label="Pricing model">{titleCase(plan.pricing_model)}</Row>
              <Row label="Multiplier">{Number(plan.price_multiplier).toFixed(2)}×</Row>
              {plan.included_minutes > 0 && (
                <Row label="Included minutes">{formatNumber(plan.included_minutes)}</Row>
              )}
              {Number(plan.overage_per_minute) > 0 && (
                <Row label="Overage / min">
                  {formatMoney(plan.overage_per_minute, plan.currency)}
                </Row>
              )}
              {Object.entries(plan.limits ?? {}).map(([key, value]) => (
                <Row key={key} label={titleCase(key)}>
                  {formatNumber(value)}
                </Row>
              ))}
            </dl>
          </Card>
        ))}
      </div>

      <Card title="Pricing models" className="mt-6">
        <dl className="space-y-2">
          {PRICING_MODELS.map(([model, description]) => (
            <div key={model} className="flex flex-wrap gap-x-3 text-sm">
              <dt className="w-40 shrink-0 font-mono text-xs text-ink-500">{model}</dt>
              <dd className="flex-1 text-ink-600 dark:text-ink-300">{description}</dd>
            </div>
          ))}
        </dl>
        <p className="mt-4 border-t border-ink-200 pt-3 text-xs text-ink-500 dark:border-ink-800 dark:text-ink-400">
          Whatever the model, the platform always records what the providers actually cost, so
          gross margin is measurable on every plan.
        </p>
      </Card>
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-500">{label}</dt>
      <dd className="text-right tabular-nums text-ink-800 dark:text-ink-100">{children}</dd>
    </div>
  );
}
